/*
 * Phone wizard input component
 * <p>
 *
 * @author faraz.durrani
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2023 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component ( {
    selector: 'reporter-uk-phone',
    templateUrl: './ui-phone.component.html',
    styleUrls: ['./uk-phone.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UkPhoneComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => UkPhoneComponent),
            multi: true
        },
    ],
    standalone: false
} )

export class UkPhoneComponent implements ControlValueAccessor, Validator, AfterViewInit {

  @Input () placeholder: string;

  constructor( private renderer: Renderer2, private elementRef: ElementRef ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return `${ this.innerValue }`.replace ( /\D/g, '' );
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  }
  onTouched = () => {
  }

  ngAfterViewInit() {
    this.initialValue = this.value;
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;
  }

  writeValue( value: any ): void {
    const newVal = `${ value }`.replace ( /\D/g, '' );
    this.innerValue = newVal;
    const numeric = newVal.replace ( /\D/g, '' );
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', numeric || undefined );
    this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );
    this.onChange ( numeric || undefined );
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }

    if ( !control.value && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    if ( control.value && this.initialValue !== control.value && !/^(0) ?\d{4} ?\d{6}$/.test(control.value)) {
      return {
        badInput: true,
        message: ' must be a valid uk phone number. It must start with 0 and must be 11 digits long.',
      };
    }
    return null;
  }
}
